import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { INotificationCenterListResponse } from 'types/api/notificationCenterApi.interface'
import { INotificationCenterSlice, INotificationItem } from 'types/notificationCenter.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors } from 'utils/helpers'
import {
  notificationSeverityLabel,
  notificationTypeCode,
  notificationTypeDef,
} from 'pages/NotificationCenter/notificationRegistry'

const initialState: INotificationCenterSlice = {
  isLoading: false,
  hasError: false,
  items: [],
  unreadCount: 0,
}

const mapResponse = (resp: INotificationCenterListResponse): { items: INotificationItem[]; unreadCount: number } => ({
  unreadCount: resp.unreadCount,
  items: (resp.items || []).map((n) => {
    const type = notificationTypeCode(n.type)
    const def = notificationTypeDef(type)
    return {
      id: n.id,
      type,
      category: def.category,
      severity: notificationSeverityLabel(n.severity) || def.severity,
      title: n.title,
      detail: n.detail || '',
      siteName: n.locationNode?.name || null,
      createdAt: n.createdAt,
      read: n.isRead,
      requiresAck: n.requiresAck,
      acknowledged: n.isAcknowledged,
      link: def.link,
    }
  }),
})

export const getNotificationCenter = createAsyncThunk(
  'notificationCenter/get',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<INotificationCenterListResponse> = await baseUrl.get('notifications', {
        params,
      })
      return mapResponse(resp.data)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const markNotificationCenterRead = createAsyncThunk(
  'notificationCenter/markRead',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.patch(`notifications/${id}/read`)
      const resp: AxiosResponse<INotificationCenterListResponse> = await baseUrl.get('notifications')
      return mapResponse(resp.data)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const markAllNotificationCenterRead = createAsyncThunk(
  'notificationCenter/markAllRead',
  async (_: void, thunkAPI) => {
    try {
      await baseUrl.post('notifications/read-all')
      const resp: AxiosResponse<INotificationCenterListResponse> = await baseUrl.get('notifications')
      return mapResponse(resp.data)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const acknowledgeNotificationCenter = createAsyncThunk(
  'notificationCenter/acknowledge',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.patch(`notifications/${id}/acknowledge`)
      const resp: AxiosResponse<INotificationCenterListResponse> = await baseUrl.get('notifications')
      return mapResponse(resp.data)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const applyPayload = (
  state: INotificationCenterSlice,
  { payload }: { payload: { items: INotificationItem[]; unreadCount: number } },
) => {
  state.isLoading = false
  state.hasError = false
  state.items = payload.items
  state.unreadCount = payload.unreadCount
}

const notificationCenterSlice = createSlice({
  name: 'notificationCenter',
  initialState,
  reducers: {},
  extraReducers: {
    [getNotificationCenter.pending.type]: (state) => {
      state.isLoading = true
      state.hasError = false
    },
    [getNotificationCenter.fulfilled.type]: applyPayload,
    [getNotificationCenter.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      state.hasError = true
      handleErrors(payload)
    },
    [markNotificationCenterRead.fulfilled.type]: applyPayload,
    [markNotificationCenterRead.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [markAllNotificationCenterRead.fulfilled.type]: applyPayload,
    [markAllNotificationCenterRead.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [acknowledgeNotificationCenter.fulfilled.type]: applyPayload,
    [acknowledgeNotificationCenter.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
  },
})

export default notificationCenterSlice.reducer
